import React from 'react';
import { snakeCase } from 'lodash';
import { useField } from 'formik';
import Select, { BaseOption } from './Select';
import { ValidationSchema, getRequiredFieldState } from 'helpers/form';
import { SelectProps } from './types';

interface SelectFieldProps<Option extends BaseOption = BaseOption>
  extends SelectProps<Option> {
  label: string;
  name?: string;
  isClearable?: boolean;
  isSearchable?: boolean;
  showRequiredFields?: boolean;
  validationSchema?: ValidationSchema;
}

/**
 * Select field for use in a Formik-wrapped form
 *
 * Value of `name` prop (or `label` as a fallback) should match the field in `initialValues` if defined in Formik
 *
 * Accepts any `InputGroup` or `ReactSelect` props and passes them through to inner components.
 */

function SelectField<Option extends BaseOption = BaseOption>({
  name,
  options,
  onChange,
  required,
  showRequiredFields,
  validationSchema,
  ...rest
}: SelectFieldProps<Option>) {
  const inputName = name || snakeCase(rest.label);
  const [field, _, { setValue }] = useField<Option['value']>(inputName);

  return (
    <Select<Option>
      name={field.name}
      options={options}
      value={options && options.find(option => option.value === field.value)}
      onChange={option => {
        option && setValue(option.value);
        if (onChange) onChange(option);
      }}
      onBlur={field.onBlur}
      formik
      required={getRequiredFieldState({
        fieldName: field.name,
        required,
        showRequiredFields,
        validationSchema
      })}
      {...rest}
    />
  );
}

export default SelectField;
